/**
 * ============================================================================
 * PAYLOAD BUILDING UTILITIES
 * Context-specific helpers for building query payloads
 * ============================================================================
 */

/**
 * Get selected department ID from auth state
 * @param {Object} rootState - Vuex root state
 * @returns {string|null} Department ID or null
 */
export function getSelectedDepartmentId (rootState) {
	return rootState?.Auth?.user?.selectedDepartmentId ?? null
}

/**
 * Check if user is admin
 * @param {Object} rootState - Vuex root state
 * @returns {boolean}
 */
export function isAdmin (rootState) {
	return rootState?.Auth?.user?.admin ?? false
}

/**
 * Build basic payload with department filter
 * @param {Object} rootState - Vuex root state
 * @param {Object} additionalPayload - Additional payload data
 * @returns {Object} Payload with department filter
 */
export function buildDepartmentPayload (rootState, additionalPayload = {}) {
	const payload = { ...additionalPayload }

	const departmentId = getSelectedDepartmentId(rootState)
	if (departmentId && !payload.departmentIds) {
		payload.departmentIds = [departmentId]
	}

	return payload
}

/**
 * Build payload with search filters
 * @param {Object} searchFilters - Search filters object
 * @param {Object} basePayload - Base payload to extend
 * @returns {Object} Payload with search filters applied
 */
export function applySearchFilters (searchFilters, basePayload = {}) {
	const payload = { ...basePayload }

	if (!searchFilters) {
		return payload
	}

	Object.entries(searchFilters).forEach(([key, value]) => {
		const shouldInclude =
            typeof value === 'boolean' ||
            (value !== undefined && (!Array.isArray(value) || value.length > 0))

		if (shouldInclude) {
			payload[key] = value
		}
	})

	return payload
}

/**
 * Build payload with department and search filters
 * @param {Object} rootState - Vuex root state
 * @param {Object} searchFilters - Search filters
 * @param {Object} additionalPayload - Additional payload data
 * @returns {Object} Complete payload
 */
export function buildQueryPayload (rootState, searchFilters, additionalPayload = {}) {
	const basePayload = buildDepartmentPayload(rootState, additionalPayload)
	return applySearchFilters(searchFilters, basePayload)
}
